import * as Sentry from '@sentry/react';

import { Box, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';

import ApiContext from 'contexts/apiContext';
import ErrorContext from 'contexts/errorContext';
import Paper from '@mui/material/Paper';
import Spinner from 'common/Spinner';
import StoreContext from 'contexts/storeContext';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { getDateFromString, getDateString } from 'utils';
import { GetApp } from '@mui/icons-material';
import { createAndDownloadPdf } from 'pdf';
import { CreditNote } from 'types/creditNote';

export default function CreditNotesTable() {
  const { api } = useContext(ApiContext);
  const { store, formatNumber } = useContext(StoreContext);
  const { setError } = useContext(ErrorContext);

  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const fetchCreditNotes = useCallback(
    (showLoader = true, pageNumber = 1, rows = rowsPerPage) => {
      setLoading(showLoader);
      api.creditNotes
        .list(store.id, { page: pageNumber, page_size: rows })
        .then((res) => {
          setCreditNotes(res.data.results);
          setPageCount(res.data.count);
        })
        .catch(setError)
        .finally(() => setLoading(false));
    },
    [api, rowsPerPage, setError, store.id],
  );

  const handleChangePage = (event: any, page: number) => {
    setCurrentPage(page + 1);
    fetchCreditNotes(true, page + 1);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const rows = parseInt(event.target.value, 10);
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchCreditNotes(true, currentPage);
  }, [currentPage, fetchCreditNotes]);

  const downloadCreditNote = (creditNote: CreditNote) => {
    setDownloadingIds((prev) => new Set(prev).add(creditNote.id));
    api.creditNotes
      .getPdf(store.id, creditNote.id)
      .then((res) => {
        createAndDownloadPdf(document, res.data, `nota_de_credito_${creditNote.id}`);
      })
      .catch((error) => {
        setError(error);
        console.error(error);
        Sentry.captureException(error);
      })
      .finally(() => {
        setDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(creditNote.id);
          return next;
        });
      });
  };

  return loading ? (
    <Box marginX={'auto'}>
      <Spinner />
    </Box>
  ) : creditNotes.length > 0 ? (
    <TableContainer component={Paper}>
      <Table aria-label='simple table'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Período</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Pedidos con pago</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Saldo</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {creditNotes.map((note) => (
            <TableRow key={note.id}>
              <TableCell>{getDateString(getDateFromString(note.created_at))}</TableCell>
              <TableCell>{`${getDateString(getDateFromString(note.period_from), undefined, true)} - ${getDateString(
                getDateFromString(note.period_to),
                undefined,
                true,
              )}`}</TableCell>
              <TableCell>{note.amount ? `$${formatNumber(note.amount)}` : 'Ver factura'}</TableCell>
              <TableCell>{note.in_app_payment_only ? 'Online' : note.in_store_payment_only ? 'En tienda' : 'Online y en tienda'}</TableCell>
              <TableCell>{note.remaining_amount ? `$${formatNumber(note.remaining_amount)}` : '-'}</TableCell>
              <TableCell>
                {downloadingIds.has(note.id) ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton
                    onClick={() => downloadCreditNote(note)}
                    aria-label='Descargar nota de crédito'
                    color='primary'
                    size='small'
                    sx={{ p: 0 }}
                  >
                    <GetApp fontSize='small' />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 20]}
        component='div'
        count={pageCount}
        rowsPerPage={rowsPerPage}
        page={currentPage - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage='Filas por página:'
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`}
      />
    </TableContainer>
  ) : (
    <Grid container justifyContent='center'>
      <Typography>Aún no tienes notas de crédito.</Typography>
    </Grid>
  );
}
